/**
 * Build a prioritized resolution queue from BreakdownState.
 * Returns max 3 questions for unresolved hard-required fields.
 */

import type { BreakdownState } from './buildBreakdownState';

const PRIORITY_1 = ['optionA', 'optionB', 'decisionType'];
const PRIORITY_2 = ['impactScore', 'urgencyScore'];
const PRIORITY_3 = ['initialPreference'];

function isFieldUnresolved(state: BreakdownState, fieldName: string): boolean {
  const field = state.fields[fieldName];
  if (!field) return true;
  if (field.detection_status === 'missing') return true;
  // BUG 10 FIX: Also check missingFields list
  if (state.missingFields?.includes(fieldName)) return true;
  const cs = field.confirmation_status;
  return !(cs === 'accepted' || cs === 'edited' || cs === 'confirmed');
}

export interface ResolutionCandidate {
  fieldName: string;
  priority: 1 | 2 | 3;
}

export function buildResolutionQueue(state: BreakdownState): ResolutionCandidate[] {
  const candidates: ResolutionCandidate[] = [];

  // Handle optionA+B as a pair
  const needsA = isFieldUnresolved(state, 'optionA');
  const needsB = isFieldUnresolved(state, 'optionB');
  if (needsA || needsB) {
    candidates.push({ fieldName: needsA && needsB ? 'optionPair' : (needsA ? 'optionA' : 'optionB'), priority: 1 });
  }

  if (isFieldUnresolved(state, 'decisionType')) {
    candidates.push({ fieldName: 'decisionType', priority: 1 });
  }

  for (const f of PRIORITY_2) {
    if (isFieldUnresolved(state, f)) {
      candidates.push({ fieldName: f, priority: 2 });
    }
  }

  for (const f of PRIORITY_3) {
    if (isFieldUnresolved(state, f)) {
      candidates.push({ fieldName: f, priority: 3 });
    }
  }

  // Sort by priority, max 5 (BUG 9 FIX: was 3, too few for 4+ unresolved fields)
  return candidates.sort((a, b) => a.priority - b.priority).slice(0, 5);
}
